import { useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  ClipboardList,
  Copy,
  MoreHorizontal,
  Receipt,
  Trash2,
  Wrench,
} from "lucide-react";
import type { DailyEntryFlags, DailyEntryOptions } from "@crewledger/shared/schemas";
import { EmptyState, FormHint } from "@/components/design";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { BETA_EMPTY_STATES } from "@/lib/beta-empty-states";
import { MICROCOPY } from "@/lib/microcopy";
import { formatCurrency, formatHours } from "@/features/dashboard/utils";
import { EquipmentSelector } from "./components/equipment-selector";
import { RowWarningsList } from "./components/row-warnings-list";
import { DailyEntryMobileBoard } from "./daily-entry-mobile-board";
import {
  buildRowWarningsForRow,
  previewRowPay,
  type DailyEntryUiRow,
} from "./utils";
import { PayBreakdownPopover } from "./pay-breakdown-popover";
import { GeofenceStatusBadge } from "./components/geofence-status-badge";

const FLAG_CONFIG: { key: keyof DailyEntryFlags; label: string; color: string }[] = [
  { key: "isWeekend", label: "Weekend", color: "bg-blue-100 text-blue-800 border-blue-200" },
  { key: "isHoliday", label: "Holiday", color: "bg-purple-100 text-purple-800 border-purple-200" },
  { key: "isNightShift", label: "Night", color: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  { key: "isHazard", label: "Hazard", color: "bg-red-100 text-red-800 border-red-200" },
  { key: "isTraining", label: "Training", color: "bg-teal-100 text-teal-800 border-teal-200" },
  { key: "isManualOvertime", label: "Manual OT", color: "bg-amber-100 text-amber-900 border-amber-200" },
  { key: "isManualDoubleTime", label: "Manual DT", color: "bg-orange-100 text-orange-900 border-orange-200" },
];

interface DailyEntryTableProps {
  rows: DailyEntryUiRow[];
  options: DailyEntryOptions;
  date: string;
  canEdit: boolean;
  canViewPayroll: boolean;
  canViewEquipmentCosts: boolean;
  selectedRowIds: Set<string>;
  onToggleRowSelected: (clientId: string) => void;
  onToggleAllRowsSelected: (selected: boolean) => void;
  onChangeRow: (clientId: string, patch: Partial<DailyEntryUiRow>) => void;
  onRemoveRow: (clientId: string) => void;
  onCopyYesterday: (clientId: string) => void;
  yesterdayRows: DailyEntryUiRow[];
  onStartFieldEntry?: () => void;
}

export function DailyEntryTable({
  rows,
  options,
  date,
  canEdit,
  canViewPayroll,
  canViewEquipmentCosts,
  selectedRowIds,
  onToggleRowSelected,
  onToggleAllRowsSelected,
  onChangeRow,
  onRemoveRow,
  onCopyYesterday,
  yesterdayRows,
  onStartFieldEntry,
}: DailyEntryTableProps) {
  const [breakdownRowId, setBreakdownRowId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const useVirtual = rows.length > 20;
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 200,
    overscan: 4,
  });
  const virtualRows = useVirtual ? rowVirtualizer.getVirtualItems() : null;
  const breakdownRow = rows.find((row) => row.clientId === breakdownRowId);
  const breakdownPreview = breakdownRow
    ? previewRowPay(breakdownRow, options, date, canViewPayroll)
    : null;

  const allSelected = rows.length > 0 && rows.every((row) => selectedRowIds.has(row.clientId));
  const columnCount = canEdit ? 12 : 11;

  const renderRow = (row: DailyEntryUiRow) => {
    const preview = previewRowPay(row, options, date, canViewPayroll);
    const warnings = buildRowWarningsForRow(row, options);
    const hasErrors = warnings.some((w) => w.severity === "error");
    const yesterday = yesterdayRows.find((y) => y.workerId === row.workerId);

    return (
      <tr
        key={row.clientId}
        className={cn(
          "border-t align-top transition-colors",
          hasErrors ? "bg-red-50/60" : warnings.length ? "bg-amber-50/40" : "hover:bg-bg-light/60",
        )}
      >
                  {canEdit ? (
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        aria-label={`Select ${row.workerName}`}
                        checked={selectedRowIds.has(row.clientId)}
                        onChange={() => { onToggleRowSelected(row.clientId); }}
                        className="rounded border-input"
                      />
                    </td>
                  ) : null}
                  <td className="px-3 py-3 font-mono text-xs font-semibold text-navy-deep">
                    {row.employeeId}
                  </td>
                  <td className="px-3 py-3">
                    <p className="font-semibold text-navy-deep">{row.workerName}</p>
                    <GeofenceStatusBadge status={row.geofenceStatus} className="mt-1" />
                    {row.crew ? <p className="text-xs text-muted-foreground">{row.crew}</p> : null}
                  </td>
                  <td className="px-3 py-3">
                    <Input
                      type="number"
                      min={0}
                      max={24}
                      step={0.5}
                      disabled={!canEdit}
                      value={row.hours}
                      onChange={(e) =>
                        { onChangeRow(row.clientId, { hours: Number(e.target.value) || 0 }); }
                      }
                      className="h-11 text-lg font-semibold tabular-nums"
                    />
                  </td>
                  <td className="px-3 py-3">
                    <Input
                      list={`job-codes-${row.clientId}`}
                      disabled={!canEdit}
                      value={row.jobCode ?? ""}
                      onChange={(e) => { onChangeRow(row.clientId, { jobCode: e.target.value }); }}
                      className="h-10 font-mono text-sm"
                      placeholder="JOB-100"
                    />
                    <datalist id={`job-codes-${row.clientId}`}>
                      {options.recentJobCodes.map((code) => (
                        <option key={code} value={code} />
                      ))}
                    </datalist>
                  </td>
                  <td className="px-3 py-3">
                    <Input
                      disabled={!canEdit}
                      value={row.costCode ?? ""}
                      onChange={(e) => { onChangeRow(row.clientId, { costCode: e.target.value }); }}
                      className="h-10 font-mono text-sm"
                      placeholder="CC-01"
                    />
                  </td>
                  <td className="px-3 py-3">
                    <div className="space-y-2">
                      {(row.equipment.length ? row.equipment : [{ equipmentId: "", usageHours: 0 }]).map(
                        (item, index) => (
                          <div key={`${row.clientId}-eq-${index}`} className="flex items-start gap-1">
                            <EquipmentSelector
                              equipment={options.equipment}
                              value={item.equipmentId}
                              usageHours={item.usageHours}
                              laborHours={row.hours}
                              canEdit={canEdit}
                              canViewEquipmentCosts={canViewEquipmentCosts}
                              onEquipmentChange={(equipmentId) => {
                                const equipment = [...row.equipment];
                                if (!equipment[index]) {
                                  equipment[index] = { equipmentId, usageHours: 0 };
                                } else {
                                  equipment[index] = { ...equipment[index], equipmentId };
                                }
                                onChangeRow(row.clientId, { equipment });
                              }}
                              onUsageHoursChange={(usageHours) => {
                                const equipment = [...row.equipment];
                                equipment[index] = { ...equipment[index]!, usageHours };
                                onChangeRow(row.clientId, { equipment });
                              }}
                            />
                            {canEdit && row.equipment.length > 1 ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="mt-1 shrink-0"
                                onClick={() => {
                                  const equipment = row.equipment.filter((_, i) => i !== index);
                                  onChangeRow(row.clientId, { equipment });
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            ) : null}
                          </div>
                        ),
                      )}
                      {canEdit ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 text-xs"
                          onClick={() =>
                            { onChangeRow(row.clientId, {
                              equipment: [...row.equipment, { equipmentId: "", usageHours: 0 }],
                            }); }
                          }
                        >
                          <Wrench className="mr-1 h-3 w-3" />
                          Add equipment
                        </Button>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="rounded-md bg-bg-light px-2 py-2 text-center text-base font-semibold tabular-nums">
                      {formatHours(row.equipment.reduce((acc, e) => acc + e.usageHours, 0))}
                    </div>
                    {row.equipment.some((e) => e.usageHours > row.hours) ? (
                      <Input
                        disabled={!canEdit}
                        placeholder="Override reason"
                        value={
                          row.equipment.find((e) => e.usageHours > row.hours)
                            ?.equipmentHoursOverrideReason ?? ""
                        }
                        onChange={(e) => {
                          const equipment = row.equipment.map((item) =>
                            item.usageHours > row.hours
                              ? { ...item, equipmentHoursOverrideReason: e.target.value }
                              : item,
                          );
                          onChangeRow(row.clientId, { equipment });
                        }}
                        className="mt-1 h-8 text-xs"
                      />
                    ) : null}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1">
                      {FLAG_CONFIG.map((flag) => (
                        <button
                          key={flag.key}
                          type="button"
                          disabled={!canEdit}
                          onClick={() =>
                            { onChangeRow(row.clientId, {
                              flags: { ...row.flags, [flag.key]: !row.flags[flag.key] },
                            }); }
                          }
                          className={cn(
                            "rounded-full border px-2 py-0.5 text-[10px] font-semibold transition-colors",
                            row.flags[flag.key]
                              ? flag.color
                              : "border-border bg-white text-muted-foreground hover:border-navy-blueprint/40",
                          )}
                        >
                          {flag.label}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <Input
                      disabled={!canEdit}
                      value={row.notes ?? ""}
                      onChange={(e) => { onChangeRow(row.clientId, { notes: e.target.value }); }}
                      className="h-10"
                      placeholder="Notes"
                    />
                  </td>
                  <td className="px-3 py-3">
                    <button
                      type="button"
                      aria-label="View pay breakdown"
                      onClick={() => { setBreakdownRowId(row.clientId); }}
                      className="w-full rounded-lg border bg-bg-light px-2 py-2 text-left transition-colors hover:border-navy-blueprint/40"
                    >
                      <div className="flex items-center gap-1 text-xs font-semibold text-navy-deep">
                        <Receipt className="h-3.5 w-3.5" />
                        {canViewPayroll ? formatCurrency(preview.grossPay ?? 0) : "—"}
                      </div>
                      <p className="mt-1 text-[10px] text-muted-foreground">
                        R {formatHours(preview.regularHours)} · OT {formatHours(preview.overtimeHours)} · DT{" "}
                        {formatHours(preview.doubleTimeHours)}
                      </p>
                      {preview.breakdown.length > 0 ? (
                        <p className="mt-1 text-[10px] font-medium text-navy-blueprint">
                          View breakdown
                        </p>
                      ) : null}
                      {warnings.length ? (
                        <RowWarningsList warnings={warnings} className="mt-1" compact />
                      ) : null}
                    </button>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-col gap-1">
                      {canEdit && yesterday ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2"
                          title="Same as yesterday"
                          onClick={() => { onCopyYesterday(row.clientId); }}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      ) : null}
                      {canEdit ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-red-danger"
                          onClick={() => { onRemoveRow(row.clientId); }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      ) : (
                        <MoreHorizontal className="mx-auto h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  </td>
                </tr>
    );
  };

  const paddingTop = virtualRows?.[0]?.start ?? 0;
  const paddingBottom =
    virtualRows && virtualRows.length > 0
      ? rowVirtualizer.getTotalSize() - (virtualRows[virtualRows.length - 1]?.end ?? 0)
      : 0;

  return (
    <>
      <DailyEntryMobileBoard
        rows={rows}
        options={options}
        date={date}
        canEdit={canEdit}
        canViewPayroll={canViewPayroll}
        canViewEquipmentCosts={canViewEquipmentCosts}
        onChangeRow={onChangeRow}
        onRemoveRow={onRemoveRow}
        onCopyYesterday={onCopyYesterday}
        onOpenBreakdown={setBreakdownRowId}
        yesterdayRows={yesterdayRows}
      />

      <div
        ref={scrollRef}
        className={cn(
          "hidden overflow-x-auto rounded-xl border-2 border-border bg-card-white shadow-[var(--shadow-card)] lg:block",
          useVirtual && "max-h-[calc(100vh-260px)] overflow-y-auto",
        )}
      >
      <table className="min-w-[1200px] w-full text-sm">
        <thead className="sticky top-0 z-10 bg-navy-deep text-left text-xs uppercase tracking-wider text-white">
          <tr>
            {canEdit ? (
              <th className="px-3 py-3 w-10">
                <input
                  type="checkbox"
                  aria-label="Select all workers"
                  checked={allSelected}
                  onChange={(e) => { onToggleAllRowsSelected(e.target.checked); }}
                  className="rounded border-white/40"
                />
              </th>
            ) : null}
            <th className="px-3 py-3">
              <span>Employee ID</span>
              <FormHint className="mt-0.5 normal-case text-white/60">{MICROCOPY.employeeIdPrivacy}</FormHint>
            </th>
            <th className="px-3 py-3">Worker</th>
            <th className="px-3 py-3 w-24">Hours</th>
            <th className="px-3 py-3">Job Code</th>
            <th className="px-3 py-3">Cost Code</th>
            <th className="px-3 py-3 min-w-[200px]">Equipment</th>
            <th className="px-3 py-3 w-20">Equipment Hours</th>
            <th className="px-3 py-3 min-w-[220px]">Flags</th>
            <th className="px-3 py-3 min-w-[140px]">Notes</th>
            <th className="px-3 py-3">Pay Preview</th>
            <th className="px-3 py-3 w-16" />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columnCount} className="p-0">
                <EmptyState
                  icon={ClipboardList}
                  title={BETA_EMPTY_STATES.fieldEntry.title}
                  description={BETA_EMPTY_STATES.fieldEntry.description}
                  actionLabel={
                    canEdit && onStartFieldEntry
                      ? BETA_EMPTY_STATES.fieldEntry.actionLabel
                      : undefined
                  }
                  onAction={canEdit ? onStartFieldEntry : undefined}
                  className="border-0"
                />
              </td>
            </tr>
          ) : useVirtual && virtualRows ? (
            <>
              {paddingTop > 0 ? (
                <tr aria-hidden>
                  <td colSpan={columnCount} style={{ height: paddingTop, padding: 0, border: 0 }} />
                </tr>
              ) : null}
              {virtualRows.map((virtualRow) => renderRow(rows[virtualRow.index]!))}
              {paddingBottom > 0 ? (
                <tr aria-hidden>
                  <td colSpan={columnCount} style={{ height: paddingBottom, padding: 0, border: 0 }} />
                </tr>
              ) : null}
            </>
          ) : (
            rows.map((row) => renderRow(row))
          )}
        </tbody>
      </table>
      </div>
      {breakdownRow && breakdownPreview ? (
        <PayBreakdownPopover
          open={breakdownRowId != null}
          onClose={() => { setBreakdownRowId(null); }}
          workerName={breakdownRow.workerName}
          grossPay={breakdownPreview.grossPay}
          equipmentCost={breakdownPreview.equipmentCost}
          breakdown={breakdownPreview.breakdown}
          payWarnings={breakdownPreview.payWarnings}
          canViewPayroll={canViewPayroll}
          canViewEquipmentCosts={canViewEquipmentCosts}
        />
      ) : null}
    </>
  );
}
